import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { ToastType } from '@components/Toast';
import { ICustomerDetails } from '@store/customers/customerDetails/types';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { Comments } from '@typeDefinitions/comments';
import { createError } from '@utils/errorUtils';
import { logError } from '@utils/errorLogging';
import { handleToast } from '@utils/utils';

import {
  ProspectData,
  ProspectDuplicateCandidateRequest,
  ProspectDuplicateCandidateResponse,
  ProspectGridApiResponse,
} from './types';

export const getDoNotSolicitCustomers = async () => {
  try {
    const response: V4ApiResponse<string[]> = await apiClient.get(
      '/Customer/getdonotsolicitcustomers',
    );
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to get do not solicit customers');
    handleToast(ToastType.Error, e.message);
  }
};

export const getProspectsGridData = async (
  payload: Record<string, unknown>,
) => {
  try {
    const response: V4ApiResponse<ProspectGridApiResponse> =
      await apiClient.post('/ProspectCustomer/getprospects', payload);
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to get prospects grid data');
    handleToast(ToastType.Error, e.message);
  }
};

export const updateProspectStatus = async (
  payload: {
    id: number;
    status: 'Quoting' | 'Contacted' | 'Lead' | 'Dead Lead';
  },
  changeTrackingSources: ChangeTrackingSource[] = [],
) => {
  try {
    const response: V4ApiResponse<null> = await apiClient.put(
      'ProspectCustomer/updatestatus',
      payload,
      buildChangeTrackingRequestConfig(changeTrackingSources),
    );
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to update prospect status');
    handleToast(ToastType.Error, e.message);
  }
};

export const getCustomerLookup = async (params: {
  name?: string;
  state?: string;
}) => {
  try {
    const response: V4ApiResponse<
      {
        name: string;
        status: string;
      }[]
    > = await apiClient.get('/Customer/Lookup', {
      params,
    });
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to get customer lookup');
    handleToast(ToastType.Error, e.message);
  }
};

export const getProspectDuplicateCandidates = async (
  payload: ProspectDuplicateCandidateRequest,
): Promise<V4ApiResponse<ProspectDuplicateCandidateResponse>> => {
  try {
    const response: V4ApiResponse<ProspectDuplicateCandidateResponse> =
      await apiClient.post('/ProspectCustomer/duplicate-candidates', payload);
    return response;
  } catch (error) {
    logError('getProspectDuplicateCandidates', error as Error);
    throw createError(error, 'Failed to get prospect duplicate candidates');
  }
};

export const fetchProspectDetails = async (id: number) => {
  try {
    const response: V4ApiResponse<ProspectData> = await apiClient.get(
      `ProspectCustomer/${id}`,
    );
    return response;
  } catch (error) {
    const err = createError(error, 'Failed to get prospect details');
    handleToast(ToastType.Error, err.message);
  }
};

export const fetchUploadedProspectDocuments = async (id: number) => {
  try {
    const response: V4ApiResponse<
      {
        name: string;
        url: string;
      }[]
    > = await apiClient.get(`ProspectCustomer/document/${id}`);
    return response;
  } catch (error) {
    const err = createError(error, 'Failed to get uploaded documents');
    handleToast(ToastType.Error, err.message);
  }
};

export const uploadProspectDocument = async (
  formData: FormData,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: V4ApiResponse<{
      results: {
        url: string;
        name: string | null;
      }[];
    }> = await apiClient.post('/ProspectCustomer/document', formData, {
      headers: {
        ...(changeTrackingSource
          ? buildChangeTrackingRequestConfig([changeTrackingSource])?.headers
          : {}),
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to upload prospect document');
    handleToast(ToastType.Error, e.message);
  }
};

export const deleteProspectDocument = async (
  payload: Record<string, unknown>,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: V4ApiResponse<null> = await apiClient.post(
      '/ProspectCustomer/deletedocument',
      payload,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to delete prospect document');
    handleToast(ToastType.Error, e.message);
  }
};

export const postProspectComment = async (
  payload: {
    comment: string;
    objectId: number;
    type: string;
  },
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: V4ApiResponse<Comments> = await apiClient.post(
      '/ProspectCustomer/Comment',
      payload,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to post prospect comment');
    handleToast(ToastType.Error, e.message);
  }
};

export const deleteProspectComment = async (
  id: number,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: V4ApiResponse<null> = await apiClient.delete(
      `/ProspectCustomer/Comment/${id}`,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );
    return response.isSuccess;
  } catch (error) {
    const e = createError(error, 'Failed to delete prospect comment');
    handleToast(ToastType.Error, e.message);
  }
};

export const updateProspectDetailsApi = async (
  payload: ProspectData,
  changeTrackingSources: ChangeTrackingSource[] = [],
) => {
  try {
    const response: V4ApiResponse<ProspectData> = await apiClient.put(
      '/ProspectCustomer',
      payload,
      buildChangeTrackingRequestConfig(changeTrackingSources),
    );
    return response;
  } catch (error) {
    const err = createError(error, 'Failed to get prospect details');
    handleToast(ToastType.Error, err.message);
  }
};

export const bulkImportProspects = async (
  file: File,
  changeTrackingSources: ChangeTrackingSource[] = [],
) => {
  try {
    const formData = new FormData();
    formData.append('formFile', file);
    const response: V4ApiResponse<{
      imported: [];
      notImported: [];
    }> = await apiClient.post('/ProspectCustomer/Import', formData, {
      headers: {
        ...buildChangeTrackingRequestConfig(changeTrackingSources)?.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to import prospects');
    handleToast(ToastType.Error, e.message);
  }
};

export const getProspectAsCustomer = async (id: number) => {
  try {
    const response: V4ApiResponse<ICustomerDetails> = await apiClient.get(
      `/ProspectCustomer/GetAsCustomer/${id}`,
    );
    return response;
  } catch (error) {
    const err = createError(error, 'Failed to get prospect as customer');
    handleToast(ToastType.Error, err.message);
  }
};

export const createProspectAsCustomer = async (
  payload: Record<string, unknown>,
  changeTrackingSources: ChangeTrackingSource[] = [],
) => {
  try {
    const response: V4ApiResponse<ICustomerDetails> = await apiClient.post(
      '/ProspectCustomer',
      payload,
      buildChangeTrackingRequestConfig(changeTrackingSources),
    );
    return response;
  } catch (error) {
    const err = createError(error, 'Failed to create prospect as customer');
    handleToast(ToastType.Error, err.message);
  }
};
